import { FC, useEffect, useState, useRef, ChangeEvent } from 'react'
import { useParams } from 'react-router-dom'
import {
  EntityPostVisibility,
  EntityPostMediaType,
  EntityTrackView,
} from '@ryddm-inc/ryddm-apiclient'

// config
import {
  minPostMessageLength,
  maxPostMessageLength,
  maxPostTracksCount,
  maxPostMediaFileSize,
} from '@/config'
// hooks
import { useAppSelector, useAppDispatch, useFile, useTracks } from '@/hooks'
// features
import {
  TrackListStudioPostUploaded,
  postSelectPostUpdateLoading,
  postSelectUploadedPosts,
  postUpdate,
} from '@/features'
// utils
import { validatePostMediaFile, validatePostMessage } from '@/utils'
// icons
import { icons } from '@/assets'
// components
import {
  Loader,
  InputArea,
  InputLabel,
  InputDescription,
  MediaFilePreview,
  ButtonMedium,
  CheckboxOption,
  Error,
} from '@/components'

// styles
import { styles } from './styles'

type FormProps = {}

export const Form: FC<FormProps> = () => {
  const { postId } = useParams()

  const dispatch = useAppDispatch()
  const postUpdateLoading = useAppSelector(postSelectPostUpdateLoading)

  const uploadedPosts = useAppSelector(postSelectUploadedPosts)
  const post = uploadedPosts.find((p) => p.id === postId)
  const {
    mediaId,
    mediaType,
    trackIds,
    message: currentMessage,
    visibility: currentVisibility,
    unlockPrice: currentUnlockPrice,
  } = post ?? {}

  const getMediaPreviewType = () => {
    if (mediaType === EntityPostMediaType.Image) {
      return 'image'
    }

    if (mediaType === EntityPostMediaType.Video) {
      return 'video'
    }

    return ''
  }

  // post tracks
  const { tracks: postTracks } = useTracks(trackIds || [])

  // media state
  const postHasMedia = !!mediaId
  const postMedia = useFile(mediaId || '')

  const [media, setMedia] = useState<File | undefined>(undefined)
  const [mediaPreview, setMediaPreview] = useState(postHasMedia ? postMedia : '')
  const [mediaPreviewType, setMediaPreviewType] = useState<'image' | 'video' | ''>(
    getMediaPreviewType(),
  )
  const mediaFileInputRef = useRef<HTMLInputElement | null>(null)
  const [mediaError, setMediaError] = useState('')

  // message state
  const [message, setMessage] = useState(currentMessage || '')
  const [messageError, setMessageError] = useState('')

  // tracks state
  const [tracks, setTracks] = useState<EntityTrackView[]>(postTracks || [])

  // visibility state
  const [visibility, setVisibility] = useState(
    currentVisibility || EntityPostVisibility.Public,
  )

  const messageUnchanged = message === currentMessage
  const tracksUnchanged =
    tracks.map((t) => t.id).join('') === postTracks?.map((t) => t.id).join('')
  const mediaNotSelected = media === undefined
  const postDefaultsUnchanged = messageUnchanged && tracksUnchanged && mediaNotSelected
  const submitDisabled =
    postDefaultsUnchanged ||
    message.length < minPostMessageLength ||
    message.length > maxPostMessageLength ||
    mediaError !== '' ||
    messageError !== '' ||
    postUpdateLoading

  const revokePreviewURL = (url: string) => {
    URL.revokeObjectURL(url)
  }

  const cleanupState = () => {
    setMedia(undefined)
    setMessage(currentMessage || '')
    setTracks(postTracks || [])
    setVisibility(currentVisibility || EntityPostVisibility.Public)
  }

  const onMediaClick = () => {
    mediaFileInputRef.current?.click()
    setMediaError('')
  }

  const onMediaFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target

    // if file selected
    if (files && files.length > 0) {
      const file = files[0]

      // validate file
      const error = validatePostMediaFile(file)
      if (error) {
        setMediaError(error)
        return
      }

      // if file of image type
      if (file.type.startsWith('image/')) {
        setMediaPreviewType('image')
      }

      // if file of video type
      if (file.type.startsWith('video/')) {
        setMediaPreviewType('video')
      }

      // if media preview was set
      if (mediaPreview) {
        // clean up to avoid memory leaks
        revokePreviewURL(mediaPreview)
      }

      // set file as a preview
      setMediaPreview(URL.createObjectURL(file))

      // store file in state
      setMedia(file)
    }
  }

  useEffect(() => () => {
    // if media preview was set
    if (mediaPreview) {
      // clean up to avoid memory leaks
      revokePreviewURL(mediaPreview)
    }
  })

  useEffect(() => {
    if (!postUpdateLoading) {
      cleanupState()
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [postUpdateLoading])

  return (
    <div css={styles().form.main}>
      <form
        css={styles().form.content.main}
        onSubmit={(e) => {
          e.preventDefault()
          dispatch(postUpdate({ post: post || {}, message, media, tracks }))
        }}
      >
        {/* media */}
        <div css={styles().form.content.media.main}>
          <div css={styles().form.content.media.label}>
            <InputLabel text="Media" />
          </div>
          <div css={styles().form.content.media.preview.main}>
            <label css={styles().form.content.media.preview.label} htmlFor="media">
              media
              <input
                name="media"
                type="file"
                id="media"
                ref={mediaFileInputRef}
                accept="image/jpeg,image/webp,image/png,video/quicktime,video/mp4,video/webm"
                onChange={(e) => onMediaFileChange(e)}
              />
            </label>
            <div css={styles().form.content.media.preview.image}>
              <MediaFilePreview
                source={mediaPreview}
                type={mediaPreviewType}
                width="100%"
                height="200px"
                alt="post media"
                onClick={() => onMediaClick()}
                formats={['JPG', 'JPEG', 'PNG', 'WEBP', 'MOV', 'MP4', 'WEBM']}
                maxFileSize={maxPostMediaFileSize}
              />
            </div>
          </div>
          <div css={styles().form.content.media.error}>
            <Error error={mediaError} />
          </div>
        </div>

        {/* message */}
        <div css={styles().form.content.message.main}>
          <div css={styles().form.content.message.label}>
            <InputLabel text="Message" />
          </div>
          <div css={styles().form.content.message.input}>
            <InputArea
              name="message"
              label="message"
              id="tip-message"
              placeholder="Share your experience with your followers"
              value={message}
              maxLength={maxPostMessageLength}
              error={messageError.length > 0}
              errorMessage={messageError}
              rows={6}
              onChange={(e) => setMessage(e.target.value)}
              onBlur={() => setMessageError(validatePostMessage(message))}
              onFocus={() => setMessageError('')}
            />
          </div>
        </div>

        {/* tracks */}
        <div css={styles().form.content.tracks.main}>
          <div css={styles().form.content.tracks.list}>
            <TrackListStudioPostUploaded
              postTracks={tracks}
              addPostTrack={(track: EntityTrackView) =>
                tracks.length < maxPostTracksCount
                  ? setTracks((state) => [...state, track])
                  : () => {}
              }
              removePostTrack={(trackId: string) =>
                setTracks((state) => state.filter((t) => t.id !== trackId))
              }
            />
          </div>
          <div css={styles().form.content.tracks.description}>
            <InputDescription
              description={`Feel free to include a maximum of ${maxPostTracksCount} tracks in your post.`}
            />
          </div>
        </div>

        {/* visibility */}
        <div css={styles().form.content.visibility.main}>
          <div css={styles().form.content.visibility.label}>
            <InputLabel text="Visibility" />
          </div>
          <div css={styles().form.content.visibility.inputs}>
            {visibility === EntityPostVisibility.Public && (
              <CheckboxOption
                name="Public"
                description="With this visibility option, the post is made publicly accessible to everyone on the platform."
                icon={icons.Unlock}
                checked={visibility === EntityPostVisibility.Public}
                onCheckboxClick={() => setVisibility(EntityPostVisibility.Public)}
              />
            )}

            {visibility === EntityPostVisibility.Subscribers && (
              <CheckboxOption
                name="Subscribers"
                description="With this visibility option, the post is exclusively accessible to subscribers or individuals who unlock it directly, unlocking the post grants the user access to all the locked tracks it contains."
                icon={icons.Lock}
                checked={visibility === EntityPostVisibility.Subscribers}
                onCheckboxClick={() => setVisibility(EntityPostVisibility.Subscribers)}
              />
            )}
          </div>
        </div>

        {/* price */}
        {visibility === EntityPostVisibility.Subscribers && (
          <div css={styles().form.content.price.main}>
            <div css={styles().form.content.price.label}>
              <InputLabel text="Price" />
            </div>
            <div css={styles().form.content.price.amount}>
              {' '}
              ${(currentUnlockPrice || 0) / 100}
            </div>
          </div>
        )}

        {/* buttons */}
        <div css={styles().form.content.buttons.main}>
          <div css={styles().form.content.buttons.submit}>
            <ButtonMedium
              type="submit"
              aria-label="create post"
              disabled={submitDisabled}
              appearance="primary"
            >
              {postUpdateLoading ? (
                <Loader width={56} height={20} appearance="secondary" />
              ) : (
                'Update'
              )}
            </ButtonMedium>
          </div>
        </div>
      </form>
    </div>
  )
}

Form.propTypes = {}
